import type { ColumnDef } from '@tanstack/vue-table';
import type { Component, ComputedRef, MaybeRefOrGetter } from 'vue';
import type { AnyItem, CollectionFieldPath, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { ColumnHeaderActionGroup } from '#layers/ui/public';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h, toValue } from 'vue';
import { createDisplayTemplateRenderPlan, fieldPathToDisplayTemplateAst, getCollectionFieldPathLabels, resolveCollectionFieldPath, toCollectionFieldPathKey, useDataModelDisplayNames } from '#layers/data-model/public';
import LayoutTableFieldPathLabel from './LayoutTableFieldPathLabel.vue';

export function useTableColumns<T extends AnyItem>(options: {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection>;
	visibleColumnPaths: MaybeRefOrGetter<CollectionFieldPath[]>;
	renderDisplayTemplate: MaybeRefOrGetter<Component>;
	getHeaderActions?: (fieldPath: CollectionFieldPath) => ColumnHeaderActionGroup[] | undefined;
}): { fieldColumns: ComputedRef<ColumnDef<T, unknown>[]> } {
	const columnHelper = createColumnHelper<T>();

	const { getFieldDisplayName } = useDataModelDisplayNames();

	// Keep TanStack descriptor identity stable across column edits.
	const fieldColumnCache = new WeakMap<object, Map<string, ColumnDef<T, unknown>>>();

	function getCachedFieldColumn(fieldPath: CollectionFieldPath): ColumnDef<T, unknown> | null {
		const collection = toValue(options.collection);
		let perCollection = fieldColumnCache.get(collection);

		if (!perCollection) {
			perCollection = new Map<string, ColumnDef<T, unknown>>();
			fieldColumnCache.set(collection, perCollection);
		}

		const columnId = toCollectionFieldPathKey(fieldPath);
		const cached = perCollection.get(columnId);

		if (cached) return cached;

		const segments = resolveCollectionFieldPath(collection, fieldPath);
		const leafSegment = segments?.at(-1);

		if (!segments || !leafSegment) return null;

		const isSortablePrimitiveColumn = fieldPath.length === 1 && 'type' in leafSegment.field;

		const renderPlan = createDisplayTemplateRenderPlan(fieldPathToDisplayTemplateAst(fieldPath), collection);

		const headerActions = options.getHeaderActions?.(fieldPath);

		const descriptor = columnHelper.display({
			id: columnId,
			header: () =>
				h(LayoutTableFieldPathLabel, {
					labels: getCollectionFieldPathLabels(collection, fieldPath, getFieldDisplayName),
				}),
			enableSorting: isSortablePrimitiveColumn,
			enableHiding: true,
			enableOrdering: true,
			enablePinning: true,
			cell: ({ row }) =>
				h(toValue(options.renderDisplayTemplate), {
					item: row.original,
					renderPlan,
				}),
			meta: headerActions ? { headerActions } : undefined,
		});

		perCollection.set(columnId, descriptor);
		return descriptor;
	}

	const fieldColumns = computed(() => toValue(options.visibleColumnPaths).flatMap((fieldPath) => {
		const descriptor = getCachedFieldColumn(fieldPath);
		return descriptor ? [descriptor] : [];
	}));

	return { fieldColumns };
}
